import React, { useState } from 'react';
import { Users, Activity, Search, Settings, Bug, ChevronDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useUser } from '../contexts/UserContext';

const AdminPanel = () => {
  const { isAdmin, userData } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testAccounts, setTestAccounts] = useState(['0']); // Default to production accounts
  
  // Data states
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  
  // Chart data states
  const [registrationData, setRegistrationData] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    users: true,
    sessions: false,
    searchHistory: false
  });
  

  // Check if user has admin access
  if (!isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  // Real API calls using actual user data from Auth0
  const apiCall = async (endpoint, params = {}) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Determine environment and use appropriate URL
      const isLocal = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';
      const baseUrl = isLocal 
        ? '/tai-khoan'                 // Development - use proxy path
        : '/tai-khoan';                 // Production - nginx proxy
      
      // Construct URL properly for relative and absolute paths
      const fullUrl = baseUrl.startsWith('http') 
        ? `${baseUrl}${endpoint}`  
        : `${window.location.origin}${baseUrl}${endpoint}`;
      const url = new URL(fullUrl);
      
      // Use real user ID from Auth0 instead of hardcoded value
      if (userData && userData.user_id) {
        url.searchParams.append('user_id', userData.user_id);
      } else {
        console.warn('No user data available for admin API call');
        throw new Error('User data not loaded');
      }
      
      // Add test_accounts parameter for all admin endpoints
      testAccounts.forEach(account => {
        url.searchParams.append('test_accounts', account);
      });
      
      // Add any additional parameters
      Object.keys(params).forEach(key => {
        url.searchParams.append(key, params[key]);
      });

      console.log('Admin API call with real user data:', {
        user_id: userData.user_id,
        endpoint: endpoint,
        url: url.toString()
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (!response.ok) {
        console.error(`API responded with status: ${response.status}`);
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log(`${endpoint} response:`, data);
      return data;
    } catch (err) {
      console.error(`API call failed for ${endpoint}:`, err);
      throw err;
    }
  };


  // Load users data
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const userData = await apiCall('/users_list');
      setUsers(userData);
      
      // Process registration data for chart
      const registrations = processRegistrationData(userData);
      setRegistrationData(registrations);
    } catch (err) {
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load sessions data
  const loadSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const sessionData = await apiCall('/login_sessions');
      setSessions(sessionData);
      
      // Process session data for chart
      const sessions = processSessionData(sessionData);
      setSessionData(sessions);
    } catch (err) {
      setError(`Failed to load sessions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load search history data
  const loadSearchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const searchData = await apiCall('/search_history');
      setSearchHistory(searchData);
      
      // Process search data for chart
      const searches = processSearchData(searchData);
      setSearchData(searches);
    } catch (err) {
      setError(`Failed to load search history: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Process registration data for chart (mimicking Streamlit logic)
  const processRegistrationData = (userData) => {
    if (!userData || userData.length === 0) return [];
    
    const registrationCounts = {};
    
    userData.forEach(user => {
      if (user.created_date) {
        // Convert to Mountain Time and extract date
        const date = new Date(user.created_date);
        const mtnDate = new Date(date.getTime() - (7 * 60 * 60 * 1000)); // Approximate MT offset
        const dateStr = mtnDate.toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: '2-digit', 
          day: '2-digit' 
        });
        
        registrationCounts[dateStr] = (registrationCounts[dateStr] || 0) + 1;
      }
    });

    return Object.entries(registrationCounts)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Process session data for chart
  const processSessionData = (sessionData) => {
    if (!sessionData || sessionData.length === 0) return [];
    
    const sessionCounts = {};
    
    sessionData.forEach(session => {
      if (session.login_time) {
        // Round to nearest hour
        const date = new Date(session.login_time);
        const mtnDate = new Date(date.getTime() - (7 * 60 * 60 * 1000)); // Approximate MT offset
        mtnDate.setMinutes(0, 0, 0); // Round to hour
        const hourStr = mtnDate.toLocaleString('en-US', { 
          month: '2-digit', 
          day: '2-digit', 
          hour: '2-digit',
          hour12: true
        });
        
        sessionCounts[hourStr] = (sessionCounts[hourStr] || 0) + 1;
      }
    });

    return Object.entries(sessionCounts)
      .map(([hour, count]) => ({ hour, count }))
      .sort((a, b) => new Date(a.hour) - new Date(b.hour));
  };

  // Process search data for chart
  const processSearchData = (searchData) => {
    if (!searchData || searchData.length === 0) return [];
    
    const searchCounts = {};
    
    searchData.forEach(search => {
      if (search.search_timestamp) {
        // Round to nearest hour
        const date = new Date(search.search_timestamp);
        const mtnDate = new Date(date.getTime() - (7 * 60 * 60 * 1000)); // Approximate MT offset
        mtnDate.setMinutes(0, 0, 0); // Round to hour
        const hourStr = mtnDate.toLocaleString('en-US', { 
          month: '2-digit', 
          day: '2-digit', 
          hour: '2-digit',
          hour12: true
        });
        
        searchCounts[hourStr] = (searchCounts[hourStr] || 0) + 1;
      }
    });

    return Object.entries(searchCounts)
      .map(([hour, count]) => ({ hour, count }))
      .sort((a, b) => new Date(a.hour) - new Date(b.hour));
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          System administration and analytics
        </p>
        <div className="mt-2 inline-flex items-center space-x-2 px-3 py-1 bg-red-100 dark:bg-red-900 rounded-full">
          <Users className="w-4 h-4 text-red-600 dark:text-red-400" />
          <span className="text-sm font-medium text-red-700 dark:text-red-300">Admin Access</span>
        </div>
      </div>

      {/* Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Test Account Filter
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={testAccounts.includes('0')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setTestAccounts([...testAccounts, '0']);
                  } else {
                    setTestAccounts(testAccounts.filter(t => t !== '0'));
                  }
                }}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Production (0)</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={testAccounts.includes('1')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setTestAccounts([...testAccounts, '1']);
                  } else {
                    setTestAccounts(testAccounts.filter(t => t !== '1'));
                  }
                }}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Test (1)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Users Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setExpandedSections(prev => ({ ...prev, users: !prev.users }))}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <Users className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Users</h2>
              <ChevronDown className={`w-4 h-4 text-gray-500 transform transition-transform ${
                expandedSections.users ? 'rotate-180' : ''
              }`} />
            </button>
            <button
              onClick={loadUsers}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Loading...' : 'Load Users'}
            </button>
          </div>
        </div>

        {expandedSections.users && users.length > 0 && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4">Registrations by Day</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={registrationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{user.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{user.user_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{user.user_email || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.user_role === 'Admin' ? 'bg-red-100 text-red-800' :
                          user.user_role === 'Dev' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.user_role || 'Basic'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{user.plan_desc || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {user.created_date ? new Date(user.created_date).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Sessions Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setExpandedSections(prev => ({ ...prev, sessions: !prev.sessions }))}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <Activity className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Login Sessions</h2>
              <ChevronDown className={`w-4 h-4 text-gray-500 transform transition-transform ${
                expandedSections.sessions ? 'rotate-180' : ''
              }`} />
            </button>
            <button
              onClick={loadSessions}
              disabled={loading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Loading...' : 'Load Sessions'}
            </button>
          </div>
        </div>

        {expandedSections.sessions && sessions.length > 0 && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4">Login Sessions by Hour</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sessionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">User ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">User Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Login Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">IP Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Browser</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Platform</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sessions.map((session, index) => {
                    // Find user data for this session
                    const user = users.find(u => u.id === session.user_id) || {};
                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{session.user_id}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{user.user_name || session.user_name || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{user.user_email || session.user_email || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {session.login_time ? new Date(session.login_time).toLocaleString() : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{session.ip_address || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{session.browser_info?.name || session.browser || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{session.platform || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Search History Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setExpandedSections(prev => ({ ...prev, searchHistory: !prev.searchHistory }))}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <Search className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Search History</h2>
              <ChevronDown className={`w-4 h-4 text-gray-500 transform transition-transform ${
                expandedSections.searchHistory ? 'rotate-180' : ''
              }`} />
            </button>
            <button
              onClick={loadSearchHistory}
              disabled={loading}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Loading...' : 'Load Search History'}
            </button>
          </div>
        </div>

        {expandedSections.searchHistory && searchHistory.length > 0 && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4">Search History by Hour</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={searchData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#8B5CF6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">User ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">User Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Search Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Function Page</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Activity Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Search Parameters</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Results Count</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase sticky top-0 bg-gray-50 dark:bg-gray-700">Response Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {searchHistory.map((search, index) => {
                    // Find user data for this search
                    const user = users.find(u => u.id === search.user_id) || {};
                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{search.user_id}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{user.user_name || search.user_name || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {search.search_timestamp ? new Date(search.search_timestamp).toLocaleString() : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{search.function_page || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{search.activity_type || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                          {search.search_parameters ? JSON.stringify(search.search_parameters) : search.search_query || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{search.results_count || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {search.response_time_ms ? `${search.response_time_ms}ms` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* API Debug Tools Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Bug className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">API Debug Tools</h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Debug API connectivity, test endpoints, and analyze system performance
          </p>
        </div>
        
        <div className="p-6">
          <div className="text-center py-8">
            <Bug className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">API Debugger Coming Soon</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              Debug tools will be available in the next update
            </p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-red-100 dark:bg-red-800 rounded-full flex items-center justify-center">
              <span className="text-red-600 dark:text-red-400 text-xs">!</span>
            </div>
            <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;